// This a copy of ra-input-rich-text but without the "Code" button
import * as React from 'react';
import { MouseEvent, useEffect, useState } from 'react';

import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatStrikethrough from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import { ToggleButton, ToggleButtonGroup, ToggleButtonGroupProps } from '@mui/material';
import { Editor } from '@tiptap/react';
import { useTranslate } from 'ra-core';
import { useTiptapEditor } from 'ra-input-rich-text';

export const CustomFormatButtons = (props: ToggleButtonGroupProps) => {
  const editor = useTiptapEditor();
  const translate = useTranslate();
  const [values, setValues] = useState<string[]>([]);

  const boldLabel = translate('ra.tiptap.bold', {
    _: 'Bold',
  });

  const italicLabel = translate('ra.tiptap.italic', {
    _: 'Italic',
  });

  const underlineLabel = translate('ra.tiptap.underline', {
    _: 'Underline',
  });

  const strikeLabel = translate('ra.tiptap.strike', {
    _: 'Strikethrough',
  });

  useEffect(() => {
    const handleUpdate = () => {
      setValues(() =>
        FormatValues.reduce((acc, value) => {
          if (editor && editor.isActive(value)) {
            acc.push(value);
          }
          return acc;
        }, [])
      );
    };

    if (editor) {
      editor.on('update', handleUpdate);
      editor.on('selectionUpdate', handleUpdate);
    }

    return () => {
      if (editor) {
        editor.off('update', handleUpdate);
        editor.off('selectionUpdate', handleUpdate);
      }
    };
  }, [editor]);

  const handleChange = (event: MouseEvent<HTMLElement>, newFormats: string[]) => {
    FormatValues.forEach((format) => {
      const shouldBeDeactivated = editor && editor.isActive(format) && !newFormats.includes(format);
      const shouldBeActivated = editor && !editor.isActive(format) && newFormats.includes(format);

      if (shouldBeDeactivated || shouldBeActivated) {
        FormatActions[format](editor);
      }
    });
  };

  return (
    <ToggleButtonGroup {...props} disabled={!editor?.isEditable} onChange={handleChange} value={values}>
      <ToggleButton value="bold" aria-label={boldLabel} title={boldLabel}>
        <FormatBold fontSize="inherit" />
      </ToggleButton>
      <ToggleButton value="italic" aria-label={italicLabel} title={italicLabel}>
        <FormatItalic fontSize="inherit" />
      </ToggleButton>
      <ToggleButton value="underline" aria-label={underlineLabel} title={underlineLabel}>
        <FormatUnderlined fontSize="inherit" />
      </ToggleButton>
      <ToggleButton value="strike" aria-label={strikeLabel} title={strikeLabel}>
        <FormatStrikethrough fontSize="inherit" />
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const FormatValues = ['bold', 'italic', 'underline', 'strike'];

const FormatActions = {
  bold: (editor: Editor) => editor.chain().focus().toggleBold().run(),
  italic: (editor: Editor) => editor.chain().focus().toggleItalic().run(),
  underline: (editor: Editor) => editor.chain().focus().toggleUnderline().run(),
  strike: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
};
